/**
 * Shared zod schemas + helpers for reminder form validation.
 *
 * The full reminder modal (`ReminderModal.tsx`) layers recurrence /
 * occurrence / end-date logic on top, but the *core* rules (title length,
 * future-only datetime) are shared with any prepared-action editor that
 * lets the user tweak a reminder before applying — currently the
 * recommendation card's `create_reminder` / `reschedule_reminder`
 * editors. Extracting this keeps the rules in one place so the inline
 * editor and the modal can never drift.
 *
 * Mirrored on the backend by ``CreateUpdateReminderSerializer`` —
 * keep these in sync if a backend rule changes.
 */
import { z } from 'zod'

/** Max characters allowed in a reminder title (mirrors backend `max_length=50`). */
export const REMINDER_TITLE_MAX_LENGTH = 50

/**
 * Title rule shared by every reminder editor.
 *
 * Hard-required, capped at 50 characters. The backend serializer
 * rejects with `"Title must be 50 characters or less"` if either
 * constraint is violated, so any FE form should catch this before
 * submit.
 */
export const reminderTitleSchema = z
  .string()
  .min(1, 'Title is required')
  .max(REMINDER_TITLE_MAX_LENGTH, 'Title must be 50 characters or less')

/**
 * Combine a YYYY-MM-DD date and an HH:MM time into a `Date`.
 * Returns null when either piece is missing or unparseable, so callers
 * can decide whether the absence is a validation error in their context
 * (the modal allows quick_schedule as an alternative).
 */
export function parseReminderRunAt(
  dateStr: string | null | undefined,
  timeStr?: string | null
): Date | null {
  if (!dateStr) return null
  const time = timeStr || '09:00'
  const parsed = new Date(`${dateStr}T${time}:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * True when `runAt` is strictly in the future relative to `now`. Mirrors
 * the backend rule (`run_at >= timezone.now()` rejects with `"Run time
 * cannot be in the past"`). Callers should treat `false` as a hard
 * validation failure with the user-facing message
 * ``REMINDER_PAST_DATETIME_MESSAGE``.
 */
export function isReminderRunAtFuture(
  runAt: Date,
  now: Date = new Date()
): boolean {
  return runAt > now
}

export const REMINDER_PAST_DATETIME_MESSAGE =
  'Reminder time must be in the future'

/**
 * YYYY-MM-DD field that must resolve to a future datetime when stamped
 * with the apply path's fixed 09:00 clock — see
 * ``apply_recommendation._apply_create_reminder``. Reused standalone
 * by reschedule editors (which don't touch the title) and composed
 * into ``reminderApplyEditSchema`` for create editors.
 */
export const reminderRunAtDateSchema = z
  .string()
  .min(1, 'Date is required')
  .refine(
    (v) => {
      const parsed = parseReminderRunAt(v, '09:00')
      return parsed != null && isReminderRunAtFuture(parsed)
    },
    { message: REMINDER_PAST_DATETIME_MESSAGE }
  )

/**
 * Standalone schema for editors that only need title + date validation
 * (no recurrence). The recommendation card's `create_reminder` editor
 * uses this directly. The full `ReminderModal` schema in
 * ``ReminderModal.tsx`` extends ``reminderTitleSchema`` with its own
 * recurrence / end-type rules and isn't expressed here.
 *
 * `runAt` is a YYYY-MM-DD string so callers can drop a single
 * ``DatePicker`` value in.
 */
export const reminderApplyEditSchema = z
  .object({
    title: reminderTitleSchema,
    runAt: reminderRunAtDateSchema,
  })
  .strict()

export type ReminderApplyEditValues = z.infer<typeof reminderApplyEditSchema>
